import React, {
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Typography,
  InputAdornment,
  TextField,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import {
  getApiErrorMessage,
  useApiGet,
  useApiSend,
} from '../../utils/httpClientTs';
import {
  getConversationMessages,
  createMessage,
} from '../../endpoints/messagesTs';
import {
  ConversationMessagesResponse,
  MessageDialogProps,
  ConversationMessageCreateRequest,
  Message,
} from '../../types/messages';
import { Send, ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Viewport offset for Main padding + app footer (see Router RootLayout). */
const THREAD_VIEWPORT_OFFSET_PX = 140;

const sortMessagesAscending = (messages: Message[]): Message[] =>
  [...messages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

const ThreadMessageRow: React.FC<MessageDialogProps> = ({
  userId,
  senderId,
  content,
  timestamp,
  senderName,
}) => {
  const isOwnMessage = userId === senderId;
  const displayName = isOwnMessage ? 'You' : senderName;
  const formattedTime = dayjs.utc(timestamp).local().format('M/D/YY h:mm A');

  return (
    <Box
      sx={{
        py: 0.75,
        wordBreak: 'break-word',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="subtitle2"
          component="span"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          {displayName}
        </Typography>
        <Typography variant="caption" component="span" color="text.secondary">
          {formattedTime}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ mt: 0.25, whiteSpace: 'pre-wrap', color: 'text.primary' }}
      >
        {content}
      </Typography>
    </Box>
  );
};

interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  isSaving: boolean;
  errorSaving: string | null;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  value,
  onChange,
  onSend,
  onKeyDown,
  isSaving,
  errorSaving,
}) => (
  <Box
    sx={{
      flexShrink: 0,
      p: 1.5,
      borderTop: 1,
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      placeholder="Message…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      multiline
      maxRows={4}
      disabled={isSaving}
      error={!!errorSaving}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={onSend}
              disabled={isSaving || value.trim() === ''}
              color="primary"
              aria-label="Send message"
            >
              <Send />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
    {errorSaving && (
      <Typography
        variant="caption"
        color="error"
        sx={{ mt: 0.5, display: 'block' }}
      >
        {errorSaving}
      </Typography>
    )}
  </Box>
);

export default function ConversationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  const prevThreadIdRef = useRef<string | undefined>(undefined);

  const {
    data: conversationMessage,
    isFetching,
    refetch,
    isError,
    error,
  } = useApiGet<ConversationMessagesResponse>(
    ['conversations', id],
    getConversationMessages,
    {
      enabled: userId !== null && id !== undefined,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    { id: id! }
  );

  const orderedMessages = useMemo(() => {
    const msgs = conversationMessage?.messages ?? [];
    return sortMessagesAscending(msgs);
  }, [conversationMessage?.messages]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  useLayoutEffect(() => {
    if (orderedMessages.length === 0) return;
    if (prevThreadIdRef.current !== id) {
      prevMessageCountRef.current = 0;
      prevThreadIdRef.current = id;
    }
    const isInitialLoad = prevMessageCountRef.current === 0;
    scrollToBottom(isInitialLoad ? 'auto' : 'smooth');
    prevMessageCountRef.current = orderedMessages.length;
  }, [id, orderedMessages.length, scrollToBottom]);

  const { mutate: sendMessage } = useApiSend<
    { success: boolean },
    ConversationMessageCreateRequest
  >(
    createMessage,
    () => {
      setIsSaving(false);
      setNewMessage('');
      setErrorSaving(null);
      void refetch();
    },
    (sendError) => {
      setErrorSaving(getApiErrorMessage(sendError));
      setIsSaving(false);
    }
  );

  const handleSendMessage = useCallback(() => {
    if (newMessage.trim() === '' || !id) {
      return;
    }
    setIsSaving(true);
    setErrorSaving(null);

    sendMessage({
      conversationId: id,
      message: newMessage.trim(),
    });
  }, [newMessage, id, sendMessage]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleBackToList = useCallback(() => {
    navigate('/conversations');
  }, [navigate]);

  const threadShellSx = {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${THREAD_VIEWPORT_OFFSET_PX}px)`,
    maxWidth: 900,
    width: '100%',
    mx: 'auto',
  } as const;

  if (isFetching && !conversationMessage) {
    return (
      <Box sx={{ ...threadShellSx, p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  /* Full-page error only when we have no cached thread (e.g. initial load failed). */
  if (!isFetching && isError && !conversationMessage) {
    return (
      <Box sx={{ ...threadShellSx, p: 3 }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {getApiErrorMessage(error)}
        </Typography>
        <Button
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={() => void refetch()}
        >
          Retry
        </Button>
        <Button variant="outlined" onClick={handleBackToList}>
          Back to Messages
        </Button>
      </Box>
    );
  }

  // API returns an error when a conversation has zero messages; composer needs a loaded thread.
  if (!conversationMessage || orderedMessages.length === 0) {
    return (
      <Box sx={{ ...threadShellSx, p: 3 }}>
        {isError && conversationMessage && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            }
          >
            Couldn&apos;t refresh: {getApiErrorMessage(error)}
          </Alert>
        )}
        <Typography>This conversation has no messages yet.</Typography>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
        >
          Back to Messages
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={threadShellSx}>
      {isError && conversationMessage && (
        <Alert
          severity="warning"
          sx={{ mx: 2, mt: 1, flexShrink: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Couldn&apos;t refresh: {getApiErrorMessage(error)}
        </Alert>
      )}

      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleBackToList}
          color="primary"
          aria-label="Back to messages"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }} noWrap>
          {conversationMessage.title}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 2,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {orderedMessages.map((message) => (
          <ThreadMessageRow
            key={message.id}
            userId={userId}
            senderId={message.sender_id}
            content={message.content}
            timestamp={message.timestamp}
            senderName={message.sender_name}
          />
        ))}
        <div ref={messagesEndRef} />
      </Box>

      <MessageComposer
        value={newMessage}
        onChange={setNewMessage}
        onSend={handleSendMessage}
        onKeyDown={handleKeyPress}
        isSaving={isSaving}
        errorSaving={errorSaving}
      />
    </Box>
  );
}
